import { supabase } from "../lib/supabase";
import { Booking, PaginationParams, StatusCount, BookingStatus } from "../types/booking";
import { SupabaseClient } from "@supabase/supabase-js";
import { enqueueBackgroundCommercialInvoiceAnalysis } from "../features/commercialInvoiceAnalysis/commercialInvoiceAnalysisService";
import { validStatuses } from "../utils/bookingStatus";

// Type cast supabase to avoid TypeScript errors
const typedSupabase = supabase as unknown as SupabaseClient;

/**
 * Build select query based on visible columns for optimization
 */
const buildSelectQuery = (visibleColumns?: string[]): string => {
  // Always include base booking fields
  const baseFields = "*";
  
  // If no visible columns specified, fetch everything (backward compatibility)
  if (!visibleColumns || visibleColumns.length === 0) {
    return `
      ${baseFields},
      transport_type_info:booking_transport_types(*),
      booking_type_info:bookings_type(*),
      company_info:company(*),
      supplier_info:booking_supplier(*),
      origin_port_info:booking_origin_port(*),
      destination_port_info:booking_destination_port(*),
      booking_incoterm_info:bookings_incoterm(*),
      agent_info:booking_agent(*),
      product_info:booking_product(*),
      container_info:containers(
        *,
        container_origin_port:booking_origin_port(*),
        container_destination_port:booking_destination_port(*),
        master_container_info:master_containers_containers(
          master_container_id,
          master_containers(
            id,
            reference
          )
        )
      ),
      invoice_info:booking_sales_invoice(
        id,
        created_at,
        currency,
        moneybird_payment_status,
        moneybird_invoice_date,
        lines:booking_sales_invoice_line(
          quantity,
          unit_price,
          vat_percentage
        )
      ),
      booking_delivery_info:booking_delivery(*),
      sendcloud_shipments:sendcloud_shipments(
        booking_id,
        label_url
      ),
      booking_purchase_orders:booking_purchase_orders(id),
      commercial_invoice_analysis:booking_commercial_invoice_analyses(
        object_path,
        likelihood_score,
        completeness_score,
        proforma_likelihood,
        document_type,
        analyzed_at,
        file_name
      )
    `;
  }

  // Track which data we need
  const needsTransportType = visibleColumns.includes("id") || visibleColumns.includes("transport_type");
  const needsBookingType = visibleColumns.includes("booking_type");
  const needsCompany = visibleColumns.includes("company");
  const needsOriginPort = visibleColumns.includes("origin");
  const needsDestinationPort = visibleColumns.includes("destination");
  const needsContainer = visibleColumns.includes("origin") || 
                        visibleColumns.includes("destination") ||
                        visibleColumns.includes("etd") ||
                        visibleColumns.includes("eta") ||
                        visibleColumns.includes("container") ||
                        visibleColumns.includes("master_container");
  const needsMasterContainer = visibleColumns.includes("master_container");
  // Always include product_info for bulk actions (can be optimized later)
  // Also include if products column is visible
  const needsProductInfo = true; // Always fetch for bulk actions in drawer
  const needsDelivery =
    visibleColumns.includes("delivery") ||
    visibleColumns.includes("postal_code") ||
    visibleColumns.includes("city");
  const needsAgent = visibleColumns.includes("agent");
  const needsInvoice = visibleColumns.includes("invoice");
  const needsSendcloud = visibleColumns.includes("sendcloud");
  // Always include CI analysis cache for admin bulk actions and CI validity column
  const needsCiAnalysis = true;

  // Build select parts
  const parts: string[] = [baseFields];

  if (needsTransportType) {
    parts.push("transport_type_info:booking_transport_types(*)");
  }
  if (needsBookingType) {
    parts.push("booking_type_info:bookings_type(*)");
  }
  if (needsCompany) {
    parts.push("company_info:company(*)");
  }
  if (needsOriginPort) {
    parts.push("origin_port_info:booking_origin_port(*)");
  }
  if (needsDestinationPort) {
    parts.push("destination_port_info:booking_destination_port(*)");
  }
  if (needsContainer) {
    if (needsMasterContainer) {
      parts.push(`container_info:containers(
        id,
        reference,
        etd,
        delayed_etd,
        eta_customer,
        container_origin_port:booking_origin_port(*),
        container_destination_port:booking_destination_port(*),
        master_container_info:master_containers_containers(
          master_container_id,
          master_containers(
            id,
            reference
          )
        )
      )`);
    } else {
      parts.push(`container_info:containers(
        id,
        reference,
        etd,
        delayed_etd,
        eta_customer,
        container_origin_port:booking_origin_port(*),
        container_destination_port:booking_destination_port(*)
      )`);
    }
  }
  if (needsProductInfo) {
    parts.push("product_info:booking_product(id)"); // Only need id for count
  }
  if (needsDelivery) {
    parts.push("booking_delivery_info:booking_delivery(*)");
  }
  if (needsAgent) {
    parts.push("agent_info:booking_agent(*)");
  }
  if (needsInvoice) {
    parts.push(`invoice_info:booking_sales_invoice(
      id,
      created_at,
      currency,
      moneybird_payment_status,
      moneybird_invoice_date,
      lines:booking_sales_invoice_line(
        quantity,
        unit_price,
        vat_percentage
      )
    )`);
  }
  if (visibleColumns.includes("purchase_orders")) {
    parts.push("booking_purchase_orders:booking_purchase_orders(id)");
  }
  if (needsSendcloud) {
    parts.push(
      "sendcloud_shipments:sendcloud_shipments(booking_id, label_url)",
    );
  }
  if (needsCiAnalysis) {
    parts.push(
      "commercial_invoice_analysis:booking_commercial_invoice_analyses(object_path, likelihood_score, completeness_score, proforma_likelihood, document_type, analyzed_at, file_name, result)"
    );
  }

  return parts.join(",");
};

/** Millis for admin "Customer ETA" sort: booking override first, else container.eta_customer */
const resolveCustomerEtaMillis = (booking: Booking): number | null => {
  if (booking.eta) {
    const ms = Date.parse(booking.eta);
    return Number.isFinite(ms) ? ms : null;
  }
  const containerInfo = booking.container_info;
  const container = Array.isArray(containerInfo)
    ? containerInfo[0]
    : containerInfo;
  if (container?.eta_customer) {
    const ms = Date.parse(container.eta_customer);
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
};

const sortBookingsByResolvedCustomerEta = (
  rows: Booking[],
  sortOrder: "ascend" | "descend",
): Booking[] => {
  const ascending = sortOrder === "ascend";
  return [...rows].sort((a, b) => {
    const ma = resolveCustomerEtaMillis(a);
    const mb = resolveCustomerEtaMillis(b);
    if (ma == null && mb == null) return 0;
    if (ma == null) return 1;
    if (mb == null) return -1;
    if (ma === mb) return 0;
    return ascending ? ma - mb : mb - ma;
  });
};

/**
 * Fetch bookings with filtering, pagination, and sorting
 */
export const fetchBookings = async (params: Partial<PaginationParams> = {}) => {
  try {
    const {
      page = 1,
      pageSize = 20,
      searchQuery,
      status,
      dateRange,
      sortField,
      sortOrder,
      companyId,
      transportTypeId,
      isConsolidation,
      containerId,
      agentId,
      bookingTypeId,
      serviceLevel,
      blNumber,
      masterContainerId,
      hasInvoice,
      deliveryType,
      sendcloudFilter,
      leancustomsFilter,
      purchaseOrderFilter,
      visibleColumns,
    } = params;

    const visibleForSelect =
      visibleColumns && visibleColumns.length > 0
        ? [...visibleColumns]
        : undefined;
    if (
      purchaseOrderFilter != null &&
      visibleForSelect &&
      !visibleForSelect.includes("purchase_orders")
    ) {
      visibleForSelect.push("purchase_orders");
    }
    if (
      deliveryType != null &&
      visibleForSelect &&
      !visibleForSelect.includes("delivery")
    ) {
      visibleForSelect.push("delivery");
    }
    if (
      sendcloudFilter != null &&
      visibleForSelect &&
      !visibleForSelect.includes("sendcloud")
    ) {
      visibleForSelect.push("sendcloud");
    }
    if (
      hasInvoice != null &&
      visibleForSelect &&
      !visibleForSelect.includes("invoice")
    ) {
      visibleForSelect.push("invoice");
    }
    // Sorting by customer ETA needs container.eta_customer; ensure container join is selected
    if (
      sortField === "eta" &&
      visibleForSelect &&
      !visibleForSelect.includes("eta")
    ) {
      visibleForSelect.push("eta");
    }

    // Calculate range for pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    // Export ignores Kolommen Aanpassen and loads every relation.
    const selectQuery = params.selectAllRelations
      ? buildSelectQuery(undefined)
      : buildSelectQuery(visibleForSelect ?? visibleColumns);

    // Full export pages a light id list, then loads details in small chunks.
    // One request for 1000 joined bookings times out (statement 57014).
    const chunkedExport = Boolean(
      params.fetchAll && params.selectAllRelations,
    );

    // Prepare the query
    let query = chunkedExport
      ? typedSupabase.from("bookings").select("id")
      : typedSupabase
          .from("bookings")
          .select(selectQuery, { count: "exact" });

    if (params.bookingIds && params.bookingIds.length > 0) {
      query = query.in("id", params.bookingIds);
    }

    // Add search filter if provided
    if (searchQuery) {
      // Extract numeric ID from EFF format (e.g., "EFF3888AIR" -> 3888)
      const effPattern = /^EFF(\d+)(AIR|SEA|.*)?$/i;
      const effMatch = searchQuery.match(effPattern);
      const extractedId = effMatch ? parseInt(effMatch[1], 10) : null;
      
      // Check if search query is a plain number
      const isNumeric = !isNaN(parseInt(searchQuery));
      const numericId = isNumeric ? parseInt(searchQuery) : null;
      
      // Build OR conditions for search
      const conditions: string[] = [];
      
      // Always search in reference field
      conditions.push(`reference.ilike.%${searchQuery}%`);
      
      // Search by extracted ID from EFF format
      if (extractedId !== null) {
        conditions.push(`id.eq.${extractedId}`);
      }
      
      // Search by numeric ID if query is a plain number
      if (numericId !== null && extractedId === null) {
        conditions.push(`id.eq.${numericId}`);
      }
      
      // Combine all conditions with OR
      query = query.or(conditions.join(','));
    }

    // Add status filter if provided and not "all"
    if (status && status !== "all") {
      query = query.eq("status", status);
    }

    // Add date range filter if provided
    if (dateRange && dateRange.length === 2) {
      query = query
        .gte("created_at", dateRange[0])
        .lte("created_at", dateRange[1]);
    }

    // Add company filter if provided
    if (companyId) {
      query = query.eq("company_id", companyId);
    }

    // Add transport type filter if provided
    if (transportTypeId) {
      query = query.eq("transport_type", transportTypeId);
    }

    // Add agent filter if provided
    if (agentId) {
      query = query.eq("agent_id", agentId);
    }

    // Add booking type filter if provided
    if (bookingTypeId) {
      query = query.eq("booking_type", bookingTypeId);
    }

    if (serviceLevel) {
      query = query.eq("service_level", serviceLevel);
    }

    // Add master container filter if provided
    if (masterContainerId) {
      // First get all container IDs that belong to this master container
      const { data: containerRelations, error: relationError } = await typedSupabase
        .from("master_containers_containers")
        .select("container_id")
        .eq("master_container_id", masterContainerId);

      if (relationError) throw relationError;

      const containerIds = containerRelations?.map(rel => rel.container_id).filter(Boolean) || [];

      if (containerIds.length > 0) {
        query = query.in("container_id", containerIds);
      } else {
        // No containers in this master container - return no results
        return { 
          data: [] as Booking[], 
          count: 0, 
          error: null 
        };
      }
    }
    // Add container filter if provided and no master container filter
    else if (containerId) {
      query = query.eq("container_id", containerId);
    }
    // Use isConsolidation if provided and neither containerId nor masterContainerId is provided
    else if (isConsolidation !== null) {
      if (isConsolidation === true) {
        // If consolidated, container_id should not be null
        query = query.not("container_id", "is", null);
      } else {
        // If not consolidated, container_id should be null
        query = query.is("container_id", null);
      }
    }

    // Add BL number filter if provided
    if (blNumber) {
      // For BL number filtering, we need to do a JOIN
      // First get all containers with this BL number
      const { data: containersWithBL, error: containerError } = await typedSupabase
        .from("containers")
        .select("id")
        .eq("bl_number", blNumber);

      if (containerError) throw containerError;

      if (containersWithBL && containersWithBL.length > 0) {
        // Get container IDs with matching BL number
        const containerIds = containersWithBL.map(c => c.id);
        // Filter bookings for these container IDs
        query = query.in("container_id", containerIds);
      } else {
        // No containers with this BL number - return no results
        return { 
          data: [] as Booking[], 
          count: 0, 
          error: null 
        };
      }
    }

    // Add sorting if provided (customer ETA uses booking.eta in DB then corrected in-memory)
    if (sortField && sortOrder) {
      const order = sortOrder === "ascend" ? "asc" : "desc";
      if (sortField === "eta") {
        query = query.order("created_at", { ascending: false });
      } else {
        query = query.order(sortField, { ascending: order === "asc" });
      }
    } else {
      // Default sorting by creation date (newest first)
      query = query.order("created_at", { ascending: false });
    }

    // Client-side filters (delivery, invoice, SendCloud) need the joined rows.
    // Export loads every batch; the list keeps the default single request.
    let allRows: Booking[] = [];
    if (chunkedExport) {
      const detailChunkSize = 80;
      let exportIds = params.bookingIds ?? [];

      if (exportIds.length === 0) {
        const idBatchSize = 1000;
        let offset = 0;
        while (true) {
          const { data, error: queryError } = await query.range(
            offset,
            offset + idBatchSize - 1,
          );
          if (queryError) throw queryError;
          const batch = (data || []) as { id: number }[];
          exportIds = exportIds.concat(batch.map((row) => row.id));
          if (batch.length < idBatchSize) break;
          offset += idBatchSize;
        }
      }

      params.onExportProgress?.(0, exportIds.length);

      for (let index = 0; index < exportIds.length; index += detailChunkSize) {
        const chunk = exportIds.slice(index, index + detailChunkSize);
        const { data, error: queryError } = await typedSupabase
          .from("bookings")
          .select(selectQuery)
          .in("id", chunk);
        if (queryError) throw queryError;
        allRows = allRows.concat((data || []) as unknown as Booking[]);
        params.onExportProgress?.(
          Math.min(index + chunk.length, exportIds.length),
          exportIds.length,
        );
      }

      const idOrder = new Map(exportIds.map((id, index) => [id, index]));
      allRows.sort(
        (a, b) => (idOrder.get(a.id) ?? 0) - (idOrder.get(b.id) ?? 0),
      );
    } else if (params.fetchAll) {
      const batchSize = 1000;
      let offset = 0;
      while (true) {
        const { data, error: queryError } = await query.range(
          offset,
          offset + batchSize - 1,
        );
        if (queryError) throw queryError;
        const batch = (data || []) as unknown as Booking[];
        allRows = allRows.concat(batch);
        if (batch.length < batchSize) break;
        offset += batchSize;
      }
    } else {
      const { data: allData, error: queryError } = await query;
      if (queryError) throw queryError;
      allRows = (allData || []) as unknown as Booking[];
    }

    let filteredData = allRows;

    // Apply delivery filtering if specified
    if (deliveryType !== null && deliveryType !== undefined) {
      if (deliveryType === 'storage') {
        // Filter for bookings WITHOUT delivery info (in storage)
        filteredData = filteredData.filter(booking =>
          !booking.booking_delivery_info ||
          (Array.isArray(booking.booking_delivery_info) && booking.booking_delivery_info.length === 0)
        );
      } else if (deliveryType === 'delivery') {
        // Filter for bookings WITH delivery info (for delivery)
        filteredData = filteredData.filter(booking =>
          booking.booking_delivery_info &&
          (Array.isArray(booking.booking_delivery_info) ? booking.booking_delivery_info.length > 0 : true)
        );
      }
    }

    // Apply SendCloud filtering if specified
    if (sendcloudFilter !== null && sendcloudFilter !== undefined) {
      if (sendcloudFilter === 'with_shipments') {
        // Filter for bookings WITH SendCloud shipments (1+ shipments)
        filteredData = filteredData.filter(booking =>
          booking.sendcloud_shipments &&
          (Array.isArray(booking.sendcloud_shipments) ? booking.sendcloud_shipments.length > 0 : true)
        );
      } else if (sendcloudFilter === 'no_shipments') {
        // Filter for bookings WITHOUT SendCloud shipments (0 shipments)
        filteredData = filteredData.filter(booking =>
          !booking.sendcloud_shipments ||
          (Array.isArray(booking.sendcloud_shipments) && booking.sendcloud_shipments.length === 0)
        );
      }
    }

    // Apply Lean Customs filtering if specified (order and/or declaration)
    if (leancustomsFilter !== null && leancustomsFilter !== undefined) {
      if (leancustomsFilter === 'with_order_declaration') {
        filteredData = filteredData.filter(booking =>
          !!(booking.leancustoms_order_id || booking.leancustoms_declaration_id)
        );
      } else if (leancustomsFilter === 'no_order_declaration') {
        filteredData = filteredData.filter(booking =>
          !booking.leancustoms_order_id && !booking.leancustoms_declaration_id
        );
      }
    }

    const purchaseOrderCount = (booking: Booking) => {
      const po = booking.booking_purchase_orders;
      if (!po) return 0;
      return Array.isArray(po) ? po.length : 0;
    };

    if (purchaseOrderFilter !== null && purchaseOrderFilter !== undefined) {
      if (purchaseOrderFilter === 'with_po') {
        filteredData = filteredData.filter(
          (booking) => purchaseOrderCount(booking) > 0,
        );
      } else if (purchaseOrderFilter === 'no_po') {
        filteredData = filteredData.filter(
          (booking) => purchaseOrderCount(booking) === 0,
        );
      }
    }

    // Apply invoice filtering if specified
    if (hasInvoice !== null && hasInvoice !== undefined) {
      filteredData = filteredData.filter((booking) => {
        const hasInvoiceData = booking.invoice_info && 
          ((Array.isArray(booking.invoice_info) && booking.invoice_info.length > 0) ||
           (typeof booking.invoice_info === 'object' && !Array.isArray(booking.invoice_info)));
        
        return hasInvoice ? hasInvoiceData : !hasInvoiceData;
      });
    }

    if (sortField === "eta" && sortOrder) {
      filteredData = sortBookingsByResolvedCustomerEta(
        filteredData,
        sortOrder,
      );
    }

    // Get the total count after filtering
    const totalCount = filteredData.length;

    if (params.fetchAll) {
      return {
        data: filteredData,
        count: totalCount,
        error: null,
      };
    }

    // Apply pagination to filtered data
    const paginatedData = filteredData.slice(from, Math.min(to + 1, filteredData.length));

    return { 
      data: paginatedData as Booking[], 
      count: totalCount, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching bookings:", error);
    return { 
      data: [] as Booking[], 
      count: 0, 
      error 
    };
  }
};

/**
 * Fetch pending bookings (status = "submitted")
 */
export const fetchPendingBookings = async () => {
  try {
    const { data, error } = await typedSupabase
      .from("bookings")
      .select(`
        *,
        transport_type_info:booking_transport_types(*),
        booking_type_info:bookings_type(*),
        company_info:company(*),
        supplier_info:booking_supplier(*),
        origin_port_info:booking_origin_port(*),
        destination_port_info:booking_destination_port(*),
        booking_incoterm_info:bookings_incoterm(*),
        agent_info:booking_agent(*),
        container_info:containers(
          *,
          container_origin_port:booking_origin_port(*),
          container_destination_port:booking_destination_port(*)
        )
      `)
      .eq("status", "submitted")
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { 
      data: data as Booking[], 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching pending bookings:", error);
    return { 
      data: [] as Booking[], 
      error 
    };
  }
};

/**
 * Fetch booking status counts
 */
export const fetchBookingStatusCounts = async () => {
  try {
    const results: StatusCount[] = [];

    for (const status of validStatuses) {
      try {
        const { count, error } = await typedSupabase
          .from('bookings')
          .select('id', { count: 'exact', head: true })
          .eq('status', status);
          
        results.push({
          status,
          count: error ? 0 : (count ?? 0)
        });
      } catch (err) {
        // If there's an error, still add the status with count 0
        console.error(`Error fetching count for status ${status}:`, err);
        results.push({ status, count: 0 });
      }
    }
    
    
    return { 
      data: results, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching booking status counts:", error);

    const results = validStatuses.map((status) => ({ status, count: 0 }));
    
    return { 
      data: results as StatusCount[], 
      error 
    };
  }
};

/**
 * Fetch total bookings count
 */
export const fetchTotalBookingsCount = async () => {
  try {
    const { count, error } = await supabase
      .from("bookings")
      .select("*", { count: "exact", head: true });

    if (error) throw error;

    return { 
      count: count || 0, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching total bookings count:", error);
    return { 
      count: 0, 
      error 
    };
  }
};

/**
 * Approve a booking
 */
export const approveBooking = async (bookingId: number) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ status: "approved" })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error approving booking:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Reject a booking with a reason
 */
export const rejectBooking = async (bookingId: number, rejectionReason: string) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({
        status: "rejected",
        rejection_reason: rejectionReason,
      })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error rejecting booking:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Fetch a single booking by ID with full details
 */
export const fetchBookingById = async (id: number) => {
  try {
    // First fetch the booking
    const { data, error } = await supabase
      .from("bookings")
      .select(`
        *,
        leancustoms_order_id,
        leancustoms_declaration_id,
        transport_type_info:booking_transport_types(*),
        booking_type_info:bookings_type(*),
        company_info:company(*),
        supplier_info:booking_supplier(*),
        origin_port_info:booking_origin_port(*),
        destination_port_info:booking_destination_port(*),
        booking_incoterm_info:bookings_incoterm(*),
        agent_info:booking_agent(*),
        container_info:containers(
          *,
          container_origin_port:booking_origin_port(*),
          container_destination_port:booking_destination_port(*),
          master_container_info:master_containers_containers(
            master_container_id,
            master_containers(id, reference)
          )
        ),
        booking_delivery_info:booking_delivery(*),
        product_info:booking_product(*),
        commercial_invoice_analysis:booking_commercial_invoice_analyses(
          object_path,
          likelihood_score,
          completeness_score,
          proforma_likelihood,
          document_type,
          analyzed_at,
          file_name,
          result
        )
      `)
      .eq("id", id)
      .single();

    if (error) throw error;

    // If booking exists and has a container_id, fetch container details
    if (data && data.container_id) {
      const { data: containerData, error: containerError } = await supabase
        .from("containers")
        .select(`
          *,
          container_origin_port:booking_origin_port(*),
          container_destination_port:booking_destination_port(*),
          master_container_info:master_containers_containers(
            master_container_id,
            master_containers(id, reference)
          )
        `)
        .eq("id", data.container_id)
        .single();

      if (containerError) {
        console.warn(`Error fetching container with ID ${data.container_id}:`, containerError);
      } else {
        // Add container data to booking
        data.container_info = containerData;
      }
    }

    return { 
      data: data as Booking, 
      error: null 
    };
  } catch (error) {
    console.error(`Error fetching booking with ID ${id}:`, error);
    return { 
      data: null, 
      error 
    };
  }
};

/**
 * Update a booking status
 */
export const updateBookingStatus = async (bookingId: number, status: BookingStatus, additionalData: Record<string, unknown> = {}) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({
        status,
        ...additionalData
      })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error(`Error updating booking status to ${status}:`, error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Bulk update booking statuses
 */
export const bulkUpdateBookingStatus = async (bookingIds: number[], status: BookingStatus, additionalData: Record<string, unknown> = {}) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({
        status,
        ...additionalData
      })
      .in("id", bookingIds);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error(`Error bulk updating booking statuses to ${status}:`, error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Update booking details
 */
export const updateBookingDetails = async (
  bookingId: number, 
  data: { 
    reference?: string | null; 
    notes?: string | null;
    status?: string | null;
    origin_pickup_date?: string | null;
    eta?: string | null;
    transport_type?: number | null;
    booking_type?: number | null;
    booking_incoterm?: number | null;
    export_clearance?: boolean | null;
    pick_up_fee?: number | null;
    commodity_inspection?: boolean | null;
    post_export_license?: number | null;
    service_level?: string | null;
    invoice_email?: string | null;
    payment_terms?: number | null;
  }
) => {
  try {
    // Separate booking-specific fields from company fields
    const {
      invoice_email,
      payment_terms,
      ...bookingData
    } = data;

    // First, get the booking to find the company_id
    const { data: booking, error: fetchError } = await supabase
      .from("bookings")
      .select("company_id")
      .eq("id", bookingId)
      .single();

    if (fetchError) throw fetchError;

    // Update booking-specific fields if any
    if (Object.keys(bookingData).length > 0) {
      const { error: bookingError } = await supabase
        .from("bookings")
        .update(bookingData)
        .eq("id", bookingId);

      if (bookingError) throw bookingError;
    }

    // Update company fields if provided and booking has a company
    if (booking?.company_id && (invoice_email !== undefined || payment_terms !== undefined)) {
      const companyUpdate: {
        invoice_email?: string | null;
        pricing_payment_term?: number | null;
      } = {};
      if (invoice_email !== undefined) companyUpdate.invoice_email = invoice_email;
      if (payment_terms !== undefined) companyUpdate.pricing_payment_term = payment_terms;

      const { error: companyError } = await supabase
        .from("company")
        .update(companyUpdate)
        .eq("id", booking.company_id);

      if (companyError) throw companyError;
    }

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error updating booking details:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Update booking commercial invoice file
 */
export const updateBookingCommercialInvoice = async (
  bookingId: number, 
  filePath: string,
  appendToExisting: boolean = false
) => {
  try {
    // Get current file path if we need to append
    let updatedFilePath = filePath;
    
    if (appendToExisting) {
      const { data, error: fetchError } = await supabase
        .from("bookings")
        .select("commercial_invoice_file")
        .eq("id", bookingId)
        .single();
      
      if (fetchError) throw fetchError;
      
      // If there's an existing file, append the new one with comma
      if (data?.commercial_invoice_file) {
        updatedFilePath = `${data.commercial_invoice_file},${filePath}`;
      }
    }
    
    const updateData = {
      commercial_invoice_file: updatedFilePath
    };

    const { error } = await supabase
      .from("bookings")
      .update(updateData)
      .eq("id", bookingId);

    if (error) throw error;

    if (!appendToExisting) {
      await supabase
        .from("booking_commercial_invoice_analyses")
        .delete()
        .eq("booking_id", bookingId);
    }

    enqueueBackgroundCommercialInvoiceAnalysis(bookingId, filePath);

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error updating commercial invoice:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Update a booking product
 * @param productId The ID of the product to update
 * @param productData The updated product data
 * @returns A promise that resolves to the update result
 */
export const updateBookingProduct = async (
  productId: number,
  productData: Partial<{
    name: string;
    hs_code: string;
    ean: string | null;
    pieces: number;
    cartons: number;
    gross_weight: number;
    cbm: number;
    value: number;
  }>
) => {
  try {
    const { data, error } = await supabase
      .from("booking_product")
      .update(productData)
      .eq("id", productId)
      .select();

    if (error) {
      console.error("Error updating booking product:", error);
      return { error, data: null };
    }

    return { data, error: null };
  } catch (error) {
    console.error("Exception updating booking product:", error);
    return { error, data: null };
  }
};

/**
 * Delete a booking product
 * @param productId The ID of the product to delete
 * @returns A promise that resolves to the deletion result
 */
export const deleteBookingProduct = async (productId: number) => {
  try {
    const { error } = await supabase
      .from("booking_product")
      .delete()
      .eq("id", productId);

    if (error) {
      console.error("Error deleting booking product:", error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error("Exception deleting booking product:", error);
    return { success: false, error };
  }
};

export const deleteBookingProducts = async (productIds: number[]) => {
  if (productIds.length === 0) {
    return { success: true, error: null };
  }

  try {
    const { error } = await supabase
      .from("booking_product")
      .delete()
      .in("id", productIds);

    if (error) {
      console.error("Error deleting booking products:", error);
      return { success: false, error };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error("Exception deleting booking products:", error);
    return { success: false, error };
  }
};

/**
 * Fetch all available booking agents
 */
export const fetchAgents = async () => {
  try {
    const { data, error } = await supabase
      .from("booking_agent")
      .select("*")
      .order("name", { ascending: true });

    if (error) throw error;

    return { 
      data, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching agents:", error);
    return { 
      data: [], 
      error 
    };
  }
};

/**
 * Update a booking's agent
 */
export const updateBookingAgent = async (bookingId: number, agentId: number | null) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ agent_id: agentId })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error updating booking agent:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Update an existing delivery information
 */
export const updateDeliveryInfo = async (
  deliveryId: number,
  deliveryData: Partial<{
    street_name: string;
    house_number: string;
    city: string;
    postal_code: string;
    country: string;
    instructions: string | null;
  }>
) => {
  try {
    const { data, error } = await supabase
      .from("booking_delivery")
      .update(deliveryData)
      .eq("id", deliveryId)
      .select();

    if (error) {
      console.error("Error updating delivery information:", error);
      return { error, data: null };
    }

    return { data, error: null };
  } catch (error) {
    console.error("Exception updating delivery information:", error);
    return { error, data: null };
  }
};

/**
 * Create new delivery information for a booking
 */
export const createDeliveryInfo = async (
  bookingId: number,
  deliveryData: {
    street_name: string;
    house_number: string;
    city: string;
    postal_code: string;
    country: string;
    instructions?: string | null;
  }
) => {
  try {
    const { data, error } = await supabase
      .from("booking_delivery")
      .insert({
        booking_id: bookingId,
        ...deliveryData
      })
      .select();

    if (error) {
      console.error("Error creating delivery information:", error);
      return { error, data: null };
    }

    return { data, error: null };
  } catch (error) {
    console.error("Exception creating delivery information:", error);
    return { error, data: null };
  }
};

/**
 * Delete delivery information
 */
export const deleteDeliveryInfo = async (deliveryId: number) => {
  try {
    const { error } = await supabase
      .from("booking_delivery")
      .delete()
      .eq("id", deliveryId);

    if (error) throw error;
    
    return { error: null };
  } catch (error) {
    console.error("Error deleting delivery info:", error);
    return { error };
  }
};

/**
 * Update supplier information
 */
export const updateBookingSupplier = async (
  supplierId: number,
  supplierData: Partial<{
    name: string;
    email: string;
    phone: string;
    address: string;
    country: string;
    province: string;
  }>
) => {
  try {
    const { data, error } = await supabase
      .from("booking_supplier")
      .update(supplierData)
      .eq("id", supplierId)
      .select();

    if (error) throw error;

    return { data: data?.[0] || null, error: null };
  } catch (error) {
    console.error("Error updating supplier:", error);
    return { data: null, error };
  }
};

const LINKABLE_BOOKING_SELECT = `
  id,
  reference,
  status,
  created_at,
  eta,
  container_id,
  company_info:company_id(id, name),
  supplier_info:supplier_id(id, name),
  transport_type_info:transport_type(id, name, abbreviation),
  booking_type_info:booking_type(id, name)
`;

export interface LinkableBooking {
  id: number;
  reference: string;
  status: string;
  created_at: string;
  eta: string | null;
  container_id: number | null;
  company_info?: { id: number; name: string };
  supplier_info?: { id: number; name: string };
  transport_type_info?: { id: number; name: string; abbreviation: string };
  booking_type_info?: { id: number; name: string };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const normalizeLinkableBooking = (booking: any): LinkableBooking => ({
  ...booking,
  company_info: Array.isArray(booking.company_info)
    ? booking.company_info[0]
    : booking.company_info,
  supplier_info: Array.isArray(booking.supplier_info)
    ? booking.supplier_info[0]
    : booking.supplier_info,
  transport_type_info: Array.isArray(booking.transport_type_info)
    ? booking.transport_type_info[0]
    : booking.transport_type_info,
  booking_type_info: Array.isArray(booking.booking_type_info)
    ? booking.booking_type_info[0]
    : booking.booking_type_info,
});

export type LinkableBookingLookupResult =
  | { status: "ok"; booking: LinkableBooking }
  | { status: "not_found" }
  | { status: "already_in_container"; booking: LinkableBooking };

/** Look up a single booking by ID for linking to a container (not limited to 1000-row fetches). */
export const lookupBookingForContainerLink = async (
  containerId: number,
  bookingId: number
): Promise<LinkableBookingLookupResult> => {
  const { data, error } = await typedSupabase
    .from("bookings")
    .select(LINKABLE_BOOKING_SELECT)
    .eq("id", bookingId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return { status: "not_found" };

  const booking = normalizeLinkableBooking(data);
  if (booking.container_id === containerId) {
    return { status: "already_in_container", booking };
  }
  return { status: "ok", booking };
};

/** Search bookings that can be linked to a container (server-side, avoids 1000-row cap). */
export const searchBookingsLinkableToContainer = async (
  containerId: number,
  options?: { search?: string; limit?: number }
): Promise<LinkableBooking[]> => {
  const limit = options?.limit ?? 50;
  const search = options?.search?.trim();

  let query = typedSupabase
    .from("bookings")
    .select(LINKABLE_BOOKING_SELECT)
    .or(`container_id.is.null,container_id.neq.${containerId}`)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (search) {
    const numericId = parseInt(search.replace(/\D/g, ""), 10);
    const looksLikeBookingId =
      !isNaN(numericId) &&
      (/^\d+$/.test(search) || /^eff/i.test(search.replace(/\s/g, "")));

    if (looksLikeBookingId) {
      query = query.eq("id", numericId);
    } else if (search.length >= 2) {
      query = query.ilike("reference", `%${search}%`);
    }
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []).map(normalizeLinkableBooking);
};

/**
 * Link a booking to a container
 */
export const linkBookingToContainer = async (bookingId: number, containerId: number) => {
  try {
    const { error } = await supabase
      .from("bookings")
      .update({ container_id: containerId })
      .eq("id", bookingId);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error linking booking to container:", error);
    return { 
      success: false, 
      error 
    };
  }
};

/**
 * Link multiple bookings to a container
 */
export const linkMultipleBookingsToContainer = async (bookingIds: number[], containerId: number) => {
  try {
    // Use update (not upsert): upsert runs INSERT … ON CONFLICT and fails agent RLS,
    // which only allows UPDATE on assigned bookings.
    const { error } = await typedSupabase
      .from("bookings")
      .update({ container_id: containerId })
      .in("id", bookingIds);

    if (error) throw error;

    return { 
      success: true, 
      error: null 
    };
  } catch (error) {
    console.error("Error linking multiple bookings to container:", error);
    return { 
      success: false, 
      error 
    };
  }
}; 